import { useId, type ReactNode } from 'react';
import { useColonnes } from './colonnes.tsx';

/**
 * Tableaux.
 *
 * ── Pourquoi un composant plutôt que du `<table>` à la main ─────────────────
 * Cinq écrans affichent des tableaux, et ils ont chaque fois répété les mêmes
 * classes — en oubliant chaque fois l'un des trois détails qui rendent un
 * tableau lisible :
 *
 * 1. **Un `<caption>`.** Invisible, mais c'est ce qu'un lecteur d'écran
 *    annonce en arrivant sur le tableau. Sans lui : « tableau, 5 colonnes,
 *    12 lignes » — et rien sur ce qu'il contient.
 * 2. **`scope` sur les en-têtes.** Sans lui, la lecture cellule par cellule
 *    n'annonce pas la colonne, et un tableau de plus de trois colonnes devient
 *    incompréhensible à l'oreille.
 * 3. **Un défilement horizontal propre.** Un tableau large qui pousse la page
 *    entière casse la mise en page ; il doit défiler dans son propre cadre.
 *
 * ── Densité ────────────────────────────────────────────────────────────────
 * Un cahier journal se consulte sur un portable de 13 pouces en salle des
 * maîtres, et se projette parfois en conseil de cycle. Les deux besoins sont
 * opposés : le composant expose donc trois densités, pilotées par une variable
 * CSS pour qu'un réglage global s'applique sans re-render.
 */

/**
 * Respiration d'une cellule.
 *
 * ── Un seul reglage, et non trois ─────────────────────────────────────────
 * Un choix « Compacte / Normale / Confortable » existait dans la barre
 * laterale. Il ne pilotait que ce composant-ci — la liste des eleves, l'equipe,
 * deux tableaux de reglages. Les ecrans de travail, eux, batissent leur tableau
 * a la main : cahier journal, progressions, programmation, evaluation, emploi
 * du temps n'en dependaient pas.
 *
 * Un reglage global qui ne change rien la ou l'on regarde n'est pas un
 * reglage : c'est une case a cocher qui fait douter de l'application. On garde
 * la valeur qui etait celle par defaut.
 */
const PADDING_CELLULE = '0.55rem 0.75rem';

export interface Colonne<T> {
  /** Clé stable : sert de `key` React et d'identifiant de largeur. */
  cle: string;
  entete: ReactNode;
  /** Contenu de la cellule. */
  cellule: (ligne: T, index: number) => ReactNode;
  /** Aligne à droite : réservé aux nombres, jamais au texte. */
  numerique?: boolean;
  /** Largeur fixe (`8rem`), sinon la colonne s'ajuste au contenu. */
  largeur?: string;
  /**
   * Colonne d'actions : sortie du flux de lecture par un lecteur d'écran qui
   * parcourt les données, et jamais alignée comme du contenu.
   */
  actions?: boolean;
}

export interface ProprietesTableau<T> {
  /** Annoncé aux lecteurs d'écran. Décrit le CONTENU, pas la structure. */
  legende: string;
  colonnes: ReadonlyArray<Colonne<T>>;
  lignes: readonly T[];
  cleLigne: (ligne: T, index: number) => string;
  /** Rendu quand `lignes` est vide. Un tableau vide sans explication inquiète. */
  vide?: ReactNode;
  /** Souligne une ligne : sélection, conflit, élément du jour. */
  tonLigne?: (ligne: T) => 'normal' | 'attention' | 'erreur' | 'selection';
  /** Alterne les fonds. À éviter en dessous de six lignes : c'est du bruit. */
  zebre?: boolean;
  /**
   * Active les colonnes redimensionnables, sous cet identifiant.
   *
   * Les largeurs sont mémorisées par écran : deux tableaux partageant un nom
   * se voleraient leurs réglages. Absent = largeurs automatiques.
   */
  redimensionnable?: string;
}

const FOND_TON: Record<string, string> = {
  normal: '',
  attention: 'bg-[var(--color-warning-soft)]',
  erreur: 'bg-[var(--color-danger-soft)]',
  selection: 'bg-[var(--color-primary-soft)]',
};

export function Tableau<T>({
  legende, colonnes, lignes, cleLigne, vide, tonLigne, zebre = false,
  redimensionnable,
}: ProprietesTableau<T>) {
  const idLegende = useId();
  /*
   * Redimensionnement OPTIONNEL, et nommé par l'appelant.
   *
   * Les largeurs sont mémorisées par écran : deux tableaux partageant un même
   * identifiant se voleraient leurs réglages. Sans nom, pas de poignée — un
   * tableau de trois colonnes courtes n'en a pas besoin.
   */
  const colonnesReglables = useColonnes(redimensionnable ?? '');

  if (lignes.length === 0 && vide) {
    return <>{vide}</>;
  }

  return (
    // `overflow-x-auto` sur un conteneur dédié : un tableau large doit défiler
    // dans son cadre, jamais pousser la page entière.
    <div
      className="-mx-1 overflow-x-auto px-1"
      // Rendu focalisable au clavier : sans cela, une personne qui ne peut pas
      // utiliser de souris ne peut pas atteindre les colonnes de droite d'un
      // tableau qui déborde.
      tabIndex={0}
      role="region"
      aria-labelledby={idLegende}
    >
      <table className="w-full border-collapse text-sm">
        <caption id={idLegende} className="sr-only">{legende}</caption>
        <thead>
          <tr className="bg-[var(--color-surface-raised)]">
            {colonnes.map((c) => (
              <th
                key={c.cle}
                scope="col"
                style={{
                  padding: PADDING_CELLULE,
                  ...(c.largeur ? { width: c.largeur } : {}),
                }}
                className={[
                  'relative border-b border-[var(--color-border)] text-xs font-semibold',
                  'uppercase tracking-wide text-[var(--color-fg-muted)]',
                  c.numerique ? 'text-right' : 'text-left',
                ].join(' ')}
              >
                {/* Une colonne d'actions n'a pas d'intitulé utile à lire :
                    on le masque visuellement sans le retirer de l'arbre. */}
                {c.actions ? <span className="sr-only">{c.entete}</span> : c.entete}
                {redimensionnable && colonnesReglables.poignee(c.cle)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((ligne, index) => {
            const ton = tonLigne?.(ligne) ?? 'normal';
            return (
              <tr
                key={cleLigne(ligne, index)}
                className={[
                  'border-b border-[var(--color-border)] last:border-b-0',
                  'transition-colors hover:bg-[var(--color-surface-hover)]',
                  ton !== 'normal'
                    ? FOND_TON[ton]!
                    : zebre && index % 2 === 1
                      ? 'bg-[var(--color-surface-zebra)]'
                      : '',
                ].join(' ')}
              >
                {colonnes.map((c) => (
                  <td
                    key={c.cle}
                    style={{ padding: PADDING_CELLULE }}
                    className={[
                      'align-top',
                      c.numerique ? 'text-right tabular-nums' : '',
                    ].join(' ')}
                  >
                    {c.cellule(ligne, index)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Groupe de boutons d'action en fin de ligne.
 *
 * Compacts et discrets tant que la ligne n'est pas survolée : une colonne
 * d'actions à pleine intensité tire l'œil plus que les données elles-mêmes.
 */
export function ActionsLigne({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap justify-end gap-0.5 whitespace-nowrap">
      {children}
    </div>
  );
}
